/**
 * Apertura de la cámara posterior con **resolución adaptativa** (004 · US1 · T028; research R-3;
 * FR-002, FR-036).
 *
 * - Se pide 1080p con `ideal`, **nunca `exact`**: un `exact` que el dispositivo no puede servir
 *   hace fallar `getUserMedia` entero en lugar de degradar.
 * - Al abrir el track se lee `getSettings()` y, si el framerate real es `< 24` o el alto real es
 *   menor que 1080, se **reabre a 1280×720** (research R-3): iOS baja el framerate agresivamente
 *   con poca luz alargando la exposición (WebKit bug 196214), y con pocas decodificaciones por
 *   segundo no da tiempo a ver 4 frames de 6. Se cede resolución para recuperar cadencia.
 * - El ROI se deriva siempre del tamaño **real** del track, no del pedido.
 * - **Nunca `advanced: [...]`** para probar capacidades: por especificación un set avanzado con una
 *   sola propiedad no soportada se ignora entero y en silencio. La vía correcta es
 *   `getSupportedConstraints()` + `getCapabilities()` + `getSettings()`, y los tres van a la
 *   telemetría de `VPP_INIT`.
 * - **No se usa `torch`** (research R-7): sobre una pantalla emisiva añade un reflejo especular
 *   justo encima del símbolo.
 */

import type { CameraFailure as ContractCameraFailure } from '@fermin/contracts';

/** Resolución preferida (research R-2). */
export const CAMERA_PRIMARY = { width: 1920, height: 1080 } as const;

/** Resolución de respaldo cuando la cámara no sostiene la cadencia (research R-3). */
export const CAMERA_FALLBACK = { width: 1280, height: 720 } as const;

/** Framerate real por debajo del cual se reabre a 720p. */
export const MIN_FRAME_RATE = 24;

/** Alto real por debajo del cual se reabre a 720p. */
export const MIN_FRAME_HEIGHT = 1080;

/**
 * Por qué no se pudo abrir la cámara. Cada motivo tiene su mensaje (FR-036) y su evento de
 * observabilidad (`CAMERA_DENIED` para `DENIED`, `CAMERA_ERROR` para los otros tres; FR-038).
 *
 * **El catálogo es del contrato** (`CameraFailureSchema` de `@fermin/contracts`), no de este
 * archivo: el motivo viaja al servidor en `POST /challenges` (`client.camera_failure`, T090), así
 * que un motivo nuevo aquí y otro allá dejarían de ser el mismo catálogo sin que nadie se entere.
 */
export type CameraFailure = ContractCameraFailure;

export class CameraError extends Error {
  readonly failure: CameraFailure;

  constructor(failure: CameraFailure, cause?: unknown) {
    super(`camera: ${failure}`, { cause });
    this.name = 'CameraError';
    this.failure = failure;
  }
}

/** Traduce el error de `getUserMedia` al motivo del catálogo. Nunca se loguea el error crudo. */
export function cameraFailureOf(error: unknown): CameraFailure {
  const name = typeof error === 'object' && error !== null ? (error as { name?: string }).name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'DENIED';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'UNAVAILABLE';
    default:
      return 'ERROR';
  }
}

/** Telemetría de `VPP_INIT`: qué pudo la cámara, no quién la usa. Nunca sale de aquí nada personal. */
export interface CameraTelemetry {
  width: number | null;
  height: number | null;
  frameRate: number | null;
  facingMode: string | null;
  /** `true` si hubo que reabrir a 720p (research R-3). */
  downgraded: boolean;
}

export interface CameraHandle {
  stream: MediaStream;
  telemetry: CameraTelemetry;
  /** Detiene todas las pistas. Idempotente. */
  stop(): void;
}

export interface OpenCameraOptions {
  /** Inyectable en pruebas; por defecto `navigator.mediaDevices`. */
  mediaDevices?: Pick<MediaDevices, 'getUserMedia'>;
}

function constraintsFor(size: { width: number; height: number }): MediaStreamConstraints {
  return {
    audio: false,
    video: {
      facingMode: 'environment',
      width: { ideal: size.width },
      height: { ideal: size.height },
      frameRate: { ideal: 30 },
    },
  };
}

function telemetryOf(stream: MediaStream, downgraded: boolean): CameraTelemetry {
  const [track] = stream.getVideoTracks();
  const settings = track?.getSettings?.() ?? {};
  return {
    width: settings.width ?? null,
    height: settings.height ?? null,
    frameRate: settings.frameRate ?? null,
    facingMode: settings.facingMode ?? null,
    downgraded,
  };
}

function stopStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/** `true` si el track entregado no sostiene la cadencia o la resolución pedidas (research R-3). */
export function needsDowngrade(telemetry: CameraTelemetry): boolean {
  const slow = telemetry.frameRate !== null && telemetry.frameRate < MIN_FRAME_RATE;
  const small = telemetry.height !== null && telemetry.height < MIN_FRAME_HEIGHT;
  return slow || small;
}

/**
 * Abre la cámara posterior y devuelve el stream ya evaluado. Lanza `CameraError` con el motivo del
 * catálogo; el llamador decide el mensaje (nunca se muestra el error del navegador).
 */
export async function openCamera(options: OpenCameraOptions = {}): Promise<CameraHandle> {
  const mediaDevices =
    options.mediaDevices ??
    (typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined);
  if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
    throw new CameraError('UNSUPPORTED');
  }

  let stream: MediaStream;
  try {
    stream = await mediaDevices.getUserMedia(constraintsFor(CAMERA_PRIMARY));
  } catch (cause) {
    throw new CameraError(cameraFailureOf(cause), cause);
  }

  let telemetry = telemetryOf(stream, false);
  if (needsDowngrade(telemetry)) {
    // Reapertura a 720p: más resolución agrava la caída de framerate, y aquí el recurso escaso son
    // las decodificaciones por segundo, no los píxeles (research R-3).
    stopStream(stream);
    try {
      stream = await mediaDevices.getUserMedia(constraintsFor(CAMERA_FALLBACK));
      telemetry = telemetryOf(stream, true);
    } catch (cause) {
      throw new CameraError(cameraFailureOf(cause), cause);
    }
  }

  let stopped = false;
  return {
    stream,
    telemetry,
    stop() {
      if (stopped) {
        return;
      }
      stopped = true;
      stopStream(stream);
    },
  };
}
